// src/components/modals/DeletePromotionModal.jsx
import { useState } from 'react'
import { Trash2, AlertTriangle, RefreshCw } from 'lucide-react'
import { Button } from '../ui/Button'
import PromotionService from '../../services/promotionService'

const DeletePromotionModal = ({ isOpen, onClose, promotion, onPromotionDeleted }) => {
  const [loading, setLoading] = useState(false)

  // Don't render if not open or no promotion
  if (!isOpen || !promotion) return null

  // Handle delete confirmation
  const handleDelete = async () => {
    try {
      setLoading(true)
      console.log('🗑️ Deleting promotion:', promotion._id)

      const response = await PromotionService.deletePromotion(promotion._id)

      if (response && response.success) {
        console.log('✅ Promotion deleted successfully')
        onPromotionDeleted()
      } else {
        throw new Error(response?.message || 'Failed to delete promotion')
      }
    } catch (error) {
      console.error('❌ Error deleting promotion:', error)
      // You might want to show an error toast here
      alert(error.message || 'Failed to delete promotion')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          {/* Content */}
          <div className="p-6">
            {/* Icon */}
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>

            {/* Title */}
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Delete Promotion
              </h3>
              <p className="text-sm text-gray-500">
                Are you sure you want to delete this promotion? This action cannot be undone.
              </p>
            </div>

            {/* Promotion Info */}
            <div className="bg-red-50 rounded-lg p-4 mb-6 border border-red-200">
              <div className="flex items-center space-x-3">
                <Trash2 className="h-5 w-5 text-red-600 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-medium text-red-900">
                    {promotion.name}
                  </div>
                  <div className="text-sm text-red-700">
                    {promotion.formattedType} • {promotion.formattedDiscount}
                  </div>
                  {promotion.isCurrentlyActive && (
                    <div className="text-xs text-red-600 font-medium mt-1">
                      ⚠️ This promotion is currently running
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Warning */}
            <div className="bg-yellow-50 rounded-lg p-3 mb-6 border border-yellow-200">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <div className="font-medium mb-1">Warning:</div>
                  <ul className="text-xs space-y-1">
                    <li>• This will permanently delete the promotion</li>
                    <li>• Any active campaigns will be stopped immediately</li>
                    <li>• Historical data will be preserved for reporting</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex space-x-3">
              <Button
                onClick={onClose}
                variant="outline"
                className="flex-1 border-gray-300"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DeletePromotionModal